import { useState } from 'react'
import StaffProductManager from '../components/StaffProductManager'
import { VendorProduct } from '../types'

export default function StaffDashboard() {
  const [products, setProducts] = useState<VendorProduct[]>([])
  const [activeTab, setActiveTab] = useState<'add-product' | 'manage-products' | 'analytics'>('add-product')

  const handleProductAdded = (product: VendorProduct) => {
    setProducts(prev => [...prev, product])
    console.log('Product added:', product)
    // Here you would typically save to your database
  }

  const tabs = [
    { id: 'add-product', label: 'Add Product', icon: '➕' },
    { id: 'manage-products', label: 'Manage Products', icon: '📦' },
    { id: 'analytics', label: 'Analytics', icon: '📊' }
  ]

  return (
    <div className="staff-dashboard">
      <div className="dashboard-header">
        <h1>Staff Dashboard</h1>
        <p>Manage products and vendor information</p>
      </div>

      <div className="dashboard-nav">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`nav-tab ${activeTab === tab.id ? 'active' : ''}`}
          >
            <span className="tab-icon">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      <div className="dashboard-content">
        {activeTab === 'add-product' && (
          <div className="tab-content">
            <StaffProductManager onProductAdded={handleProductAdded} />
          </div>
        )}

        {activeTab === 'manage-products' && (
          <div className="tab-content">
            <div className="products-list">
              <h2>Recently Added Products</h2>
              {products.length === 0 ? (
                <div className="empty-state">
                  <p>No products added yet. Use the "Add Product" tab to get started.</p>
                </div>
              ) : (
                <div className="products-grid">
                  {products.map(product => (
                    <div key={product.id} className="product-card">
                      <div className="product-header">
                        <h3>{product.name}</h3>
                        <span className={`status-badge ${product.inStock ? 'in-stock' : 'out-of-stock'}`}>
                          {product.inStock ? 'In Stock' : 'Out of Stock'}
                        </span>
                      </div>
                      <p className="product-type">{product.type} • {product.extractType}</p>
                      <p className="product-description">{product.description.substring(0, 100)}...</p>
                      <div className="product-meta">
                        <span>Added: {new Date(product.dateAdded).toLocaleDateString()}</span>
                        <span>{product.sizes.length} size{product.sizes.length !== 1 ? 's' : ''}</span>
                        {product.featured && <span className="featured-badge">Featured</span>}
                      </div>
                      <div className="product-actions">
                        <button className="edit-btn">Edit</button>
                        <button className="delete-btn">Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="tab-content">
            <div className="analytics-section">
              <h2>Product Analytics</h2>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-number">{products.length}</div>
                  <div className="stat-label">Total Products Added</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">{products.filter(p => p.inStock).length}</div>
                  <div className="stat-label">In Stock</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">{products.filter(p => p.featured).length}</div>
                  <div className="stat-label">Featured Products</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">{new Set(products.map(p => p.extractType)).size}</div>
                  <div className="stat-label">Product Types</div>
                </div>
              </div>
              
              <div className="analytics-chart">
                <h3>Products by Type</h3>
                <div className="chart-placeholder">
                  <p>Chart visualization would go here</p>
                  <p>Integration with charting library needed</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .staff-dashboard {
          max-width: 1200px;
          margin: 0 auto;
          padding: 20px;
        }

        .dashboard-header {
          text-align: center;
          margin-bottom: 30px;
        }

        .dashboard-header h1 {
          color: #2c5530;
          margin-bottom: 10px;
        }

        .dashboard-nav {
          display: flex;
          justify-content: center;
          margin-bottom: 30px;
          border-bottom: 2px solid #eee;
        }

        .nav-tab {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 15px 25px;
          background: none;
          border: none;
          border-bottom: 3px solid transparent;
          cursor: pointer;
          font-size: 16px;
          color: #a0aec0;
          transition: all 0.3s ease;
        }

        .nav-tab:hover {
          color: #2c5530;
          background: #f5f5f5;
        }

        .nav-tab.active {
          color: #2c5530;
          border-bottom-color: #2c5530;
          background: #f8fdf8;
        }

        .tab-icon {
          font-size: 18px;
        }

        .dashboard-content {
          min-height: 600px;
        }

        .tab-content {
          animation: fadeIn 0.3s ease;
        }

        @keyframes fadeIn {
          0% { opacity: 0; transform: translateY(10px); }
          100% { opacity: 1; transform: translateY(0); }
        }

        .products-list {
          background: #2d3748;
          border-radius: 8px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          padding: 30px;
        }

        .products-list h2 {
          color: #2c5530;
          margin-bottom: 20px;
        }

        .empty-state {
          text-align: center;
          padding: 40px;
          color: #a0aec0;
        }

        .products-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
          gap: 20px;
        }

        .product-card {
          border: 1px solid #4a5568;
          border-radius: 8px;
          padding: 20px;
          background: #2d3748;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }

        .product-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(0,0,0,0.1);
        }

        .product-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 10px;
        }

        .product-header h3 {
          color: #2c5530;
          margin: 0;
          flex: 1;
        }

        .status-badge {
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 600;
        }

        .status-badge.in-stock {
          background: #e8f5e9;
          color: #2e7d32;
        }

        .status-badge.out-of-stock {
          background: #ffebee;
          color: #c62828;
        }

        .product-type {
          color: #a0aec0;
          font-size: 14px;
          margin: 5px 0;
        }

        .product-description {
          color: #e2e8f0;
          font-size: 14px;
          line-height: 1.4;
          margin: 10px 0;
        }

        .product-meta {
          display: flex;
          gap: 15px;
          align-items: center;
          font-size: 12px;
          color: #a0aec0;
          margin: 15px 0;
          flex-wrap: wrap;
        }

        .featured-badge {
          background: #2d37483e0;
          color: #f57c00;
          padding: 2px 6px;
          border-radius: 3px;
          font-weight: 600;
        }

        .product-actions {
          display: flex;
          gap: 10px;
          margin-top: 15px;
        }

        .edit-btn, .delete-btn {
          flex: 1;
          padding: 8px 16px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
        }

        .edit-btn {
          background: #e3f2fd;
          color: #1976d2;
        }

        .edit-btn:hover {
          background: #bbdefb;
        }

        .delete-btn {
          background: #ffebee;
          color: #d32f2f;
        }

        .delete-btn:hover {
          background: #ffcdd2;
        }

        .analytics-section {
          background: #2d3748;
          border-radius: 8px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
          padding: 30px;
        }

        .analytics-section h2 {
          color: #2c5530;
          margin-bottom: 30px;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
          margin-bottom: 40px;
        }

        .stat-card {
          background: linear-gradient(135deg, #2c5530 0%, #4caf50 100%);
          color: white;
          padding: 25px;
          border-radius: 8px;
          text-align: center;
        }

        .stat-number {
          font-size: 2.5em;
          font-weight: bold;
          margin-bottom: 10px;
        }

        .stat-label {
          font-size: 14px;
          opacity: 0.9;
        }

        .analytics-chart {
          border-top: 1px solid #eee;
          padding-top: 30px;
        }

        .analytics-chart h3 {
          color: #2c5530;
          margin-bottom: 20px;
        }

        .chart-placeholder {
          background: #f9f9f9;
          border: 2px dashed #ddd;
          border-radius: 8px;
          padding: 60px;
          text-align: center;
          color: #a0aec0;
        }

        .chart-placeholder p {
          margin: 10px 0;
        }
      `}</style>
    </div>
  )
}